// First party consent store, shared by every improve.online site, so a choice
// made here also applies in the app and the partner hub. It records
// { analytics, marketing }, and nothing non essential loads until it says so
// (UK PECR / UK GDPR):
//   analytics  Google Analytics (Improve Newco's own GA4, through Tag Manager)
//   marketing  third party embeds: the YouTube and Synthesia videos today
//
// The cookie lives on .improve.online under a name per environment
// (VUE_APP_CONSENT_COOKIE: improve_consent_v2 on production,
// improve_consent_v2_stg on staging), so the two never read each other's
// choice. It is a NEW name rather than a version bump: the old host only
// cookie and a domain cookie with the same name would both be sent, and
// whichever came first would win.
//
// Plan: docs/plans/analytics-newco-replacement-2026-10.md in the tracker repo.

const COOKIE_NAME = process.env.VUE_APP_CONSENT_COOKIE || 'improve_consent_v2';
const LEGACY_COOKIE = 'improve_consent'; // version 1, host only, until October 2026
const SHARED_DOMAIN = 'improve.online';
const MAX_AGE_DAYS = 180;

function onSharedDomain() {
  if (typeof location === 'undefined') return false;
  const host = location.hostname;
  return host === SHARED_DOMAIN || host.endsWith('.' + SHARED_DOMAIN);
}

function attributes(maxAgeSeconds) {
  const domain = onSharedDomain() ? '; domain=.' + SHARED_DOMAIN : '';
  // Secure on HTTPS only: on plain http localhost it would stop the cookie
  // being stored at all.
  const secure =
    typeof location !== 'undefined' && location.protocol === 'https:' ? '; Secure' : '';
  return '; max-age=' + maxAgeSeconds + '; path=/; SameSite=Lax' + domain + secure;
}

function read() {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp('(?:^|; )' + COOKIE_NAME + '=([^;]*)'));
  if (!match) return null;
  try {
    const obj = JSON.parse(decodeURIComponent(match[1]));
    return obj && typeof obj === 'object' ? obj : null;
  } catch (e) {
    return null;
  }
}

export function getConsent() {
  return read();
}

// True once the visitor has made any choice (so the banner should stay hidden).
export function hasResponded() {
  return read() !== null;
}

export function hasAnalytics() {
  const c = read();
  return !!(c && c.analytics);
}

export function hasMarketing() {
  const c = read();
  return !!(c && c.marketing);
}

export function setConsent({ analytics = false, marketing = false } = {}) {
  const value = JSON.stringify({ analytics: !!analytics, marketing: !!marketing, ts: Date.now() });
  document.cookie =
    COOKIE_NAME + '=' + encodeURIComponent(value) + attributes(MAX_AGE_DAYS * 24 * 60 * 60);
}

// Drops what the Sensolearn era setup left on the visitor's device: the
// version 1 consent cookie (that choice covered their tags, so every visitor
// is asked again) and the affiliate id the homepage used to store.
export function forgetLegacyConsent() {
  if (typeof document === 'undefined') return;
  document.cookie = LEGACY_COOKIE + '=; max-age=0; path=/';
  try {
    window.localStorage.removeItem('refID');
  } catch (e) {
    /* storage blocked: nothing to remove */
  }
}
